/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx (the `Form`
 * + `FormFooter` shell every FORM screen in this migration uses) and
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx (the
 * page-with-back-button + FormGroup field layout), same pairing `tiers/New.tsx` and
 * `catalogue/brands/New.tsx` cite.
 *
 * Only the data, columns and labels are ours.
 */
import { useState } from "react";
import { useLoaderData, useOutletContext } from "react-router-dom";
import { z } from "zod";
import toast from "react-hot-toast";
import { PiUserPlusDuotone as InviteIcon } from "react-icons/pi";
import FormFooter from "@core/components/form-footer";
import { createInvitation } from "../../lib/services/invitations";
import { listGrantableRoles } from "../../lib/identity";
import { roleName } from "../../lib/format";
import { can } from "../../lib/permissions";
import { formCard, formCardFooter, formSections } from "../../lib/form-layout";
import { SectionTitle } from "../../components/section-title";
import type { AccountSummary, Permission, PermissionState } from "../../types";
import { PageHeader, ErrorSurface, problemTitle, Field, FormGroup, Form } from "@steelyard/theme";
import { SelectField } from "../../components/select-field";
import { Text } from "rizzui";
import { useGoBack } from "../../lib/navigation";

// The code every invitation route requires (see List.tsx); a member of the `Permission` union.
const INVITE_PERMISSION: Permission = "company.user.invite";

export async function newInvitationLoader() {
  const roles = await listGrantableRoles();
  return { roles };
}

const invitationSchema = z.object({
  email: z.string().trim().min(1, "Email address is required").email("Invalid email address"),
  role: z.string().trim().min(1, "Role is required"),
});
type InvitationFormValues = z.infer<typeof invitationSchema>;

export default function NewInvitation() {
  const goBack = useGoBack("/invitations");
  const { roles } = useLoaderData() as Awaited<ReturnType<typeof newInvitationLoader>>;
  const [error, setError] = useState<unknown>(null);
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();

  if (!can(permissionState, INVITE_PERMISSION)) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="send an invitation" />;
  }

  return (
    <div className="flex max-w-4xl flex-col gap-6 @container">
      <PageHeader title="New Invitation" breadcrumb={[{ name: "Home", href: "/" }, { name: "Invitations", href: "/invitations" }, { name: "New Invitation" }]} />
      <Text className="max-w-2xl text-gray-500">
        The account belongs to your own company; only a caller the role matrix names may invite into another one. A
        role your own role may not grant, or an address that already has an account, comes back as a refusal below.
      </Text>

      {error ? <ErrorSurface error={error} action="send this invitation" /> : null}

      <Form<InvitationFormValues>
        validationSchema={invitationSchema}
        useFormProps={{ defaultValues: { email: "", role: "" } }}
        className={formCard}
        onSubmit={async (values) => {
          setError(null);
          try {
            await createInvitation(values.email, values.role);
            toast.success("Invitation sent");
            goBack();
          } catch (err) {
            setError(err);
            toast.error(problemTitle(err, "Could not send the invitation"));
          }
        }}
      >
        {({ register, formState: { errors, isSubmitting } }) => (
          <>
            <div className={formSections}>
              <FormGroup title={<SectionTitle icon={InviteIcon}>Invitee</SectionTitle>} description="Who this invitation goes to, and what role their account starts with.">
                <Field
                  label="Email"
                  required
                  type="email"
                  autoComplete="off"
                  placeholder="name@company.com"
                  error={errors.email?.message}
                  {...register("email")}
                />
                <SelectField
                  label="Role"
                  required
                  disabled={roles.length === 0}
                  disabledReason={roles.length === 0 ? "No roles available to grant." : undefined}
                  hint="Only roles your own role may grant appear here."
                  error={errors.role?.message}
                  {...register("role")}
                >
                  <option value="">{roles.length === 0 ? "No roles available" : "Choose a role…"}</option>
                  {roles.map((role) => (
                    <option key={role} value={role}>
                      {roleName(role)}
                    </option>
                  ))}
                </SelectField>
              </FormGroup>
            </div>

            <FormFooter
              className={formCardFooter}
              submitBtnText={isSubmitting ? "Sending…" : "Send invitation"}
              isLoading={isSubmitting}
              altBtnText="Cancel"
              handleAltBtn={goBack}
            />
          </>
        )}
      </Form>
    </div>
  );
}
